import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

const TITLE_BG = "#1F4E78";
const HEADER_BG = "#305496";
const BORDER = "#000000";
const ROW_ALT_BG = "#F5F8FC";
const FOOTER_COLOR = "#555555";

const styles = StyleSheet.create({
  page: {
    paddingTop: 18,
    paddingBottom: 28,
    paddingHorizontal: 16,
    fontFamily: "kokila",
    fontSize: 8,
  },
  titleBar: {
    backgroundColor: TITLE_BG,
    paddingVertical: 10,
    paddingHorizontal: 8,
    marginBottom: 6,
    width: "100%",
  },
  titleText: {
    color: "#FFFFFF",
    fontFamily: "kokila",
    fontSize: 14,
    fontWeight: "bold",
    textAlign: "center",
  },
  subTitleText: {
    color: TITLE_BG,
    fontFamily: "kokila",
    fontSize: 12,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 4,
  },
  dateRangeText: {
    color: TITLE_BG,
    fontFamily: "kokila",
    fontSize: 10,
    textAlign: "center",
    marginBottom: 12,
  },
  table: {
    width: "100%",
    borderWidth: 1,
    borderColor: BORDER,
    borderStyle: "solid",
  },
  tableRow: {
    flexDirection: "row",
    width: "100%",
    alignItems: "stretch",
  },
  tableHeaderRow: {
    flexDirection: "row",
    width: "100%",
    backgroundColor: HEADER_BG,
    alignItems: "stretch",
  },
  footer: {
    fontFamily: "kokila",
    marginTop: 14,
    fontSize: 9,
    color: FOOTER_COLOR,
    fontStyle: "italic",
    textAlign: "center",
  },
});

const cellText = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return String(value);
};

const getCellStyle = (isHeader, align, widthPercent) => ({
  width: `${widthPercent}%`,
  borderRightWidth: 1,
  borderRightColor: BORDER,
  borderBottomWidth: 1,
  borderBottomColor: BORDER,
  borderStyle: "solid",
  paddingVertical: 6,
  paddingHorizontal: 4,
  justifyContent: "center",
  minHeight: isHeader ? 30 : 26,
  ...(isHeader ? { backgroundColor: HEADER_BG } : {}),
});

const getTextStyle = (isHeader, align) => ({
  fontFamily: "kokila",
  fontSize: isHeader ? 8 : 7,
  fontWeight: isHeader ? "bold" : "normal",
  color: isHeader ? "#FFFFFF" : "#000000",
  textAlign: align || "center",
  lineHeight: 1.35,
});

const TableCell = ({ widthPercent, isHeader, align, zebraBg, children }) => (
  <View
    style={{
      ...getCellStyle(isHeader, align, widthPercent),
      ...(zebraBg && !isHeader ? { backgroundColor: zebraBg } : {}),
    }}
  >
    <Text style={getTextStyle(isHeader, align)}>{children}</Text>
  </View>
);

export const ReportPdfDocument = ({
  headerText,
  reportTitle,
  dateRangeText,
  columns = [],
  rows = [],
  footerText,
  orientation = "landscape",
}) => (
  <Document>
    <Page size="A4" orientation={orientation} style={styles.page} wrap>
      {headerText ? (
        <View style={styles.titleBar}>
          <Text style={styles.titleText}>{headerText}</Text>
        </View>
      ) : null}

      {reportTitle ? (
        <Text style={styles.subTitleText}>{reportTitle}</Text>
      ) : null}

      {dateRangeText ? (
        <Text style={styles.dateRangeText}>{dateRangeText}</Text>
      ) : null}

      <View style={styles.table}>
        <View style={styles.tableHeaderRow} fixed>
          {columns.map((col, index) => (
            <TableCell
              key={col.key || index}
              widthPercent={col.widthPercent}
              isHeader
              align={col.align}
            >
              {col.label}
            </TableCell>
          ))}
        </View>

        {rows.map((row, rowIndex) => {
          const zebraBg = rowIndex % 2 === 1 ? ROW_ALT_BG : "#FFFFFF";
          return (
            <View key={rowIndex} style={styles.tableRow} wrap>
              {columns.map((col, colIndex) => (
                <TableCell
                  key={col.key || colIndex}
                  widthPercent={col.widthPercent}
                  align={col.align}
                  zebraBg={zebraBg}
                >
                  {cellText(row[col.key])}
                </TableCell>
              ))}
            </View>
          );
        })}
      </View>

      {footerText ? <Text style={styles.footer}>{footerText}</Text> : null}
    </Page>
  </Document>
);
